import { useId, useState } from 'react'
import { ChevronDown, SlidersHorizontal } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import type { SocialPlatform } from '@digital-twins/shared-types'
import { LibraryToolbar, LIBRARY_TOOLBAR_CONTROL_CLASS } from '@/components/library/LibraryToolbar'
import { LibrarySearch } from '@/components/library/LibrarySearch'
import { Button } from '@/components/ui/button'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { SocialTopicListFacet, SocialTopicListSort, SocialTopicListToolbarProps } from '@/types/components/social/topicListFilters'
import { marketDisplayName, marketFlagCode } from '../libraryHelpers'
import { SocialFilterMenu } from './SocialFilterPopover'
import { SocialFilterToken } from './SocialFilterPill'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { formatSocialLanguages, SOCIAL_PLATFORM_LABELS } from './socialFormat'
import { TOPIC_LIST_FACETS, TOPIC_LIST_SORTS } from './topicListFilters'

const PRIMARY: Array<SocialTopicListFacet> = ['market', 'brand', 'theme', 'channel']
const SECONDARY: Array<SocialTopicListFacet> = ['status', 'language', 'cadence']
const KEY = 'socialListening.topicTab.filters'

/** Filters narrow topic cards only. Counts and charts within a topic retain their existing scope. */
export function SocialTopicListToolbar(props: SocialTopicListToolbarProps) {
  const { filters, options, resultCount, totalCount, onFilterChange, onSearchChange, onSortChange, onClear } = props
  const { t, i18n } = useTranslation('app')
  const [openMenu, setOpenMenu] = useState<SocialTopicListFacet | 'sort' | null>(null)
  const [menuQuery, setMenuQuery] = useState('')
  const [showMore, setShowMore] = useState(false)
  const moreId = useId()
  const formatCount = new Intl.NumberFormat(i18n.language)
  const active = TOPIC_LIST_FACETS.filter(key => filters[key] !== null)
  const extraCount = SECONDARY.filter(key => filters[key] !== null).length
  const hasChanges = active.length > 0 || filters.q.trim().length > 0 || filters.sort !== 'updated'

  const labelFor = (key: SocialTopicListFacet, value: string): string => {
    if (key === 'market') return marketDisplayName(value, i18n.language)
    if (key === 'channel') return SOCIAL_PLATFORM_LABELS[value as SocialPlatform] ?? value
    if (key === 'language') return formatSocialLanguages([value], i18n.language)
    if (key === 'status') return t(`${KEY}.statuses.${value}`, { defaultValue: value })
    if (key === 'cadence') return t(`socialListening.form.cadence.${value}`, { defaultValue: value })
    return value
  }
  const valueLabel = (key: SocialTopicListFacet, value: string) => (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {key === 'market' && <CountryFlag code={marketFlagCode(value)} />}
      {key === 'channel' && <SocialPlatformIcon platform={value as SocialPlatform} className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{labelFor(key, value)}</span>
    </span>
  )
  const menuChange = (key: SocialTopicListFacet | 'sort') => (open: boolean) => {
    setOpenMenu(current => open ? key : current === key ? null : current)
    setMenuQuery('')
  }
  const control = (key: SocialTopicListFacet) => {
    const label = t(`${KEY}.${key}`)
    const selected = filters[key]
    // A saved filter stays removable after the last matching topic disappears or is edited.
    const values = selected && !options[key].some(option => option.value === selected)
      ? [...options[key], { value: selected, count: 0 }] : options[key]
    const searchable = key === 'brand' || key === 'theme' || key === 'market'
    const query = menuQuery.trim().toLocaleLowerCase()
    return (
      <SocialFilterMenu key={key} label={label} selected={selected} isOpen={openMenu === key} onOpenChange={menuChange(key)}
        onSelect={value => onFilterChange(key, value)} testId={`topic-list-filter-${key}`}
        search={searchable ? { value: menuQuery, onChange: setMenuQuery, placeholder: t(`${KEY}.searchOptions`), label: t(`${KEY}.searchOptions`) } : undefined}
        options={[
          { value: null, label: t(`${KEY}.any`), testId: `topic-list-option-${key}-any` },
          ...values.filter(option => !query || labelFor(key, option.value).toLocaleLowerCase().includes(query)).map(option => ({
            value: option.value, label: valueLabel(key, option.value), measure: option.count, isDisabled: option.count === 0,
            measureLabel: formatCount.format(option.count), testId: `topic-list-option-${key}-${option.value}`,
          })),
        ]}
        trigger={<button type="button" disabled={values.length === 0} title={selected ? labelFor(key, selected) : label}
          aria-label={selected ? `${label}: ${labelFor(key, selected)}` : label}
          data-testid={`topic-list-filter-${key}`}
          className={cn(LIBRARY_TOOLBAR_CONTROL_CLASS, 'inline-flex max-w-[180px] min-w-0 items-center gap-1.5 border disabled:opacity-50',
            selected ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground')}>
          {selected ? valueLabel(key, selected) : <span>{label}</span>}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </button>} />
    )
  }
  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="topic-list-toolbar">
      <LibraryToolbar filters={<>
        {PRIMARY.map(control)}
        <Button variant="outline" className={cn(LIBRARY_TOOLBAR_CONTROL_CLASS, 'gap-1.5 font-normal text-muted-foreground')}
          aria-expanded={showMore} aria-controls={showMore ? moreId : undefined} data-testid="topic-list-more-filters-toggle"
          onClick={() => { setShowMore(value => !value); setOpenMenu(null); setMenuQuery('') }}>
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          {t(`${KEY}.more`)}{extraCount > 0 ? ` (${extraCount})` : ''}
        </Button>
      </>} search={<LibrarySearch compact value={filters.q} onChange={onSearchChange} placeholder={t('socialListening.topicTab.searchPlaceholder')} />} />
      {showMore && <div id={moreId} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-background p-3">
        {SECONDARY.map(control)}
      </div>}
      {active.length > 0 && <div className="flex flex-wrap items-center gap-2" data-testid="topic-list-active-filters">
        {active.map(key => <SocialFilterToken key={key} label={t(`${KEY}.${key}`)} value={valueLabel(key, filters[key]!)}
          removeLabel={t(`${KEY}.remove`, { label: t(`${KEY}.${key}`), value: labelFor(key, filters[key]!) })}
          onRemove={() => onFilterChange(key, null)} testId={`topic-list-active-${key}`} />)}
      </div>}
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Text size="sm" color="muted" aria-live="polite" data-testid="topic-list-result-count">
            {resultCount === null ? '' : t(`${KEY}.resultCount`, { count: totalCount, visible: resultCount })}
          </Text>
          {hasChanges && <Button variant="ghost" size="sm" onClick={() => { setOpenMenu(null); setMenuQuery(''); onClear() }} data-testid="topic-list-clear-all">
            {t(`${KEY}.clearAll`)}
          </Button>}
        </div>
        <SocialFilterMenu label={t(`${KEY}.sort`)} options={TOPIC_LIST_SORTS.map(value => ({ value, label: t(`${KEY}.sorts.${value}`), testId: `topic-list-sort-${value}` }))}
          selected={filters.sort} onSelect={value => onSortChange(value as SocialTopicListSort)} isOpen={openMenu === 'sort'} onOpenChange={menuChange('sort')}
          testId="topic-list-sort" trigger={<button type="button" className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-md px-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t(`${KEY}.sortBy`, { value: t(`${KEY}.sorts.${filters.sort}`) })} data-testid="topic-list-sort-trigger">
            <span className="truncate">{t(`${KEY}.sorts.${filters.sort}`)}</span><ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>} />
      </div>
    </div>
  )
}
